import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  BookOpen,
  Crown,
  Feather,
  Lock,
  LogIn,
  Moon,
  Music4,
  NotebookPen,
  Radar,
  Shield,
  Sparkles,
  Sun,
} from "lucide-react";
import { Shelf, MobileTabs } from "@/components/mobile/Shelf";
import { readRecent, type RecentEntry } from "@/lib/recent-tools";
import { getJournalProgress, type JournalProgress } from "@/lib/journal-progress.functions";
import { useMembership } from "@/hooks/useMembership";
import { journalAccent } from "@/lib/journal-accents";
import { LIBRARY_PROMPTS, DEPTH_LABELS } from "@/lib/journal-prompt-library";

export const Route = createFileRoute("/journals")({
  head: () => ({
    meta: [
      { title: "Journal Library — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Every Recovery Trauma™ journal in one place: recovery writing, trigger tracking, inner child work, shadow work, dreams, music and memory — swipe through and pick up where you left off.",
      },
      { property: "og:title", content: "Journal Library — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "All your journals in one calm library — continue writing, or start a new one whenever you're ready.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/journals" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/journals" }],
  }),
  component: JournalsPage,
});

type Group = "all" | "triggers" | "inner-child" | "shadow" | "recovery";

type JournalCard = {
  to: string;
  label: string;
  blurb: string;
  icon: typeof NotebookPen;
  groups: Group[];
};

const JOURNALS: JournalCard[] = [
  {
    to: "/journal",
    label: "Recovery Journal",
    blurb: "Free writing with trauma-informed prompts, whenever words arrive.",
    icon: NotebookPen,
    groups: ["recovery"],
  },
  {
    to: "/guided-journal",
    label: "Guided Prompts",
    blurb: "A gentle question to start from when the page feels too blank.",
    icon: Feather,
    groups: ["recovery"],
  },
  {
    to: "/trigger-journal",
    label: "Trigger Journal",
    blurb: "What set it off, what your body did, and what actually helped.",
    icon: Radar,
    groups: ["triggers"],
  },
  {
    to: "/music-journal",
    label: "Music, Memory & Healing",
    blurb: "Your life soundtrack, and the memories each song carries.",
    icon: Music4,
    groups: ["recovery"],
  },
  {
    to: "/inner-child",
    label: "Inner Child Work",
    blurb: "Meeting the younger part of you with something other than criticism.",
    icon: Sun,
    groups: ["inner-child"],
  },
  {
    to: "/shadow-journey",
    label: "Shadow Work Journey",
    blurb: "Twelve steps through the parts you were taught to hide.",
    icon: Moon,
    groups: ["shadow"],
  },
  {
    to: "/shadow-work",
    label: "Shadow Work Prompts",
    blurb: "Standalone reflections you can take one at a time.",
    icon: Sparkles,
    groups: ["shadow"],
  },
  {
    to: "/abuse-journal",
    label: "Abuse & Safety Journal",
    blurb: "A private, careful record — at whatever pace feels bearable.",
    icon: Shield,
    groups: ["triggers"],
  },
  {
    to: "/dissociation-journal",
    label: "Dissociation Journal",
    blurb: "Notice when you drift, and what brings you back.",
    icon: BookOpen,
    groups: ["triggers"],
  },
  {
    to: "/sleep-dreams",
    label: "Dream Journal",
    blurb: "Catch the dream before the day washes it away.",
    icon: Moon,
    groups: ["recovery"],
  },
];

const TABS: { key: Group; label: string }[] = [
  { key: "all", label: "All journals" },
  { key: "triggers", label: "Trauma & Triggers" },
  { key: "inner-child", label: "Inner Child" },
  { key: "shadow", label: "Shadow Work" },
  { key: "recovery", label: "Recovery & Reflection" },
];

function relDay(iso: string): string {
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${days < 14 ? "" : "s"} ago`;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(then);
}

function JournalsPage() {
  const [group, setGroup] = useState<Group>("all");
  const [recent, setRecent] = useState<RecentEntry[]>([]);
  const membership = useMembership();
  const { isAuthed } = membership;
  // Locked preview: everyone who is not a paying Member (and not an admin)
  // still sees the whole library — titles, descriptions and sample prompts —
  // with a clear way to unlock it. While membership is still resolving we do
  // not lock, so a paying Member never sees a lock flash on their own page.
  const locked =
    !membership.loading && !membership.isMember && !membership.isAdmin;
  const fetchProgress = useServerFn(getJournalProgress);

  const { data: progress } = useQuery<JournalProgress[]>({
    queryKey: ["journal-progress"],
    queryFn: () => fetchProgress(),
    enabled: isAuthed,
    staleTime: 60_000,
  });

  const byRoute = new Map((progress ?? []).map((p) => [p.to, p]));
  const inProgress = (progress ?? [])
    .filter((p) => p.entries > 0)
    .sort((a, b) => (b.lastAt ?? "").localeCompare(a.lastAt ?? ""))
    .map((p) => ({ p, card: JOURNALS.find((j) => j.to === p.to) }))
    .filter((x): x is { p: JournalProgress; card: JournalCard } => Boolean(x.card));

  useEffect(() => {
    setRecent(readRecent().filter((r) => JOURNALS.some((j) => j.to === r.to)).slice(0, 6));
  }, []);

  const shown = group === "all" ? JOURNALS : JOURNALS.filter((j) => j.groups.includes(group));

  if (locked) return <LockedLibrary isAuthed={isAuthed} />;

  return (
    <div className="min-h-screen pb-28">
      <div className="mx-auto max-w-5xl px-4 pt-5 sm:px-6">
        <header className="mt-3">
          <h1 className="font-display text-[2rem] leading-tight text-foreground sm:text-4xl">
            Your journal library
          </h1>
          <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            Everything you write here stays private. Swipe through, pick one, and stop whenever
            you want to.
          </p>
        </header>

        <div className="mt-4 space-y-8">
          {inProgress.length > 0 && (
            <Shelf
              id="journals-in-progress"
              title="In progress"
              hint="Journals you've already started."
              cols={3}
              cardWidth="sm"
            >
              {inProgress.map(({ p, card }) => (
                <Link
                  key={p.to}
                  to={card.to}
                  className="group flex flex-col rounded-[20px] border border-gold/30 bg-card p-4 shadow-sm transition active:scale-[0.99]"
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
                      style={{
                        backgroundColor: journalAccent(card.to).tint,
                        color: journalAccent(card.to).ink,
                      }}
                    >
                      <card.icon className="h-4.5 w-4.5" aria-hidden />
                    </span>
                    <span
                      className="text-[11px] font-semibold uppercase tracking-[0.16em]"
                      style={{ color: journalAccent(card.to).ink }}
                    >
                      In progress
                    </span>
                  </span>
                  <span className="mt-2 font-display text-lg leading-snug text-foreground">
                    {card.label}
                  </span>
                  <span className="mt-1 text-[13px] leading-relaxed text-foreground/70">
                    {p.entries} {p.entries === 1 ? "entry" : "entries"}
                    {p.lastAt ? ` · last written ${relDay(p.lastAt)}` : ""}
                  </span>
                  <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-[var(--gold-deep)]">
                    Continue <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                </Link>
              ))}
            </Shelf>
          )}

          {recent.length > 0 && (
            <Shelf
              id="continue-journaling"
              title="Continue journaling"
              hint="Where you were last."
              cols={3}
              cardWidth="sm"
            >
              {recent.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="group flex flex-col rounded-[20px] border border-gold/25 bg-card p-4 shadow-sm transition active:scale-[0.99]"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--gold-deep)]">
                    Recently opened
                  </span>
                  <span className="mt-2 font-display text-lg leading-snug text-foreground">
                    {r.label}
                  </span>
                  <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-[var(--gold-deep)]">
                    Continue <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                </Link>
              ))}
            </Shelf>
          )}

          <section aria-labelledby="library-heading">
            <h2
              id="library-heading"
              className="font-display text-[1.35rem] leading-tight text-foreground sm:text-2xl"
            >
              Browse by theme
            </h2>
            <MobileTabs tabs={TABS} value={group} onChange={setGroup} label="Journal themes" />

            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3">
              {shown.map((j) => (
                <Link
                  key={j.to}
                  to={j.to}
                  className="group relative flex flex-col gap-2 overflow-hidden rounded-[20px] border border-border/70 p-4 shadow-sm transition active:scale-[0.99] sm:flex-row sm:items-start sm:gap-3"
                  style={{
                    backgroundImage: `linear-gradient(160deg, ${journalAccent(j.to).tint} 0%, var(--card) 68%)`,
                  }}
                >
                  <span
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl"
                    style={{
                      backgroundColor: "var(--card)",
                      color: journalAccent(j.to).ink,
                      boxShadow: `inset 0 0 0 1px ${journalAccent(j.to).ink}`,
                    }}
                  >
                    <j.icon className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display text-[1.05rem] leading-snug text-foreground">
                      {j.label}
                    </span>
                    <span className="mt-1 hidden text-[13px] leading-relaxed text-foreground/70 sm:block">
                      {j.blurb}
                    </span>
                    {(byRoute.get(j.to)?.entries ?? 0) > 0 && (
                      <span
                        className="mt-1.5 inline-flex items-center rounded-full bg-card px-2 py-0.5 text-[11px] font-semibold"
                        style={{ color: journalAccent(j.to).ink }}
                      >
                        In progress · {byRoute.get(j.to)!.entries}
                      </span>
                    )}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <Shelf
            id="journal-entries"
            title="My recent entries"
            hint="Your saved writing lives inside each journal."
            cols={2}
            cardWidth="lg"
          >
            <Link
              to="/journal"
              className="flex flex-col rounded-[20px] border border-gold/25 bg-card p-5 shadow-sm"
            >
              <span className="font-display text-lg text-foreground">Recovery Journal entries</span>
              <span className="mt-1 text-[13px] leading-relaxed text-foreground/70">
                Open your entry list, search what you&apos;ve written, and keep going.
              </span>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold-deep)]">
                Open journal <ArrowRight className="h-4 w-4" aria-hidden />
              </span>
            </Link>
            <Link
              to="/trigger-journal"
              className="flex flex-col rounded-[20px] border border-gold/25 bg-card p-5 shadow-sm"
            >
              <span className="font-display text-lg text-foreground">Trigger Map & entries</span>
              <span className="mt-1 text-[13px] leading-relaxed text-foreground/70">
                See the patterns your own entries have quietly been showing you.
              </span>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold-deep)]">
                Open Trigger Journal <ArrowRight className="h-4 w-4" aria-hidden />
              </span>
            </Link>
          </Shelf>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Locked preview — shown to guests and signed-in non-Members.        */
/* Everything is visible except the writing itself, so nobody hits a  */
/* dead end: every card leads to the way in.                          */
/* ------------------------------------------------------------------ */

const SAMPLE_PROMPTS = LIBRARY_PROMPTS.filter((p) => p.depth === "gentle").slice(0, 3);

function LockedLibrary({ isAuthed }: { isAuthed: boolean }) {
  return (
    <div className="min-h-screen pb-28">
      <div className="mx-auto max-w-5xl px-4 pt-5 sm:px-6">
        <header className="mt-3">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--gold-deep)]">
            <Lock className="h-3 w-3" aria-hidden /> Members only
          </span>
          <h1 className="mt-3 font-display text-[2rem] leading-tight text-foreground sm:text-4xl">
            The journal library
          </h1>
          <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            Ten private journals and over 172 trauma-informed prompts. Writing and saving
            reflections is part of Membership — here&apos;s everything that opens up when you join.
          </p>
        </header>

        <section className="mt-5 rounded-3xl border-2 border-gold/40 bg-gradient-to-br from-[oklch(0.98_0.015_85)] via-card to-[oklch(0.95_0.04_82)] p-5 shadow-[var(--shadow-gold)] sm:p-7">
          <h2 className="font-display text-xl text-foreground sm:text-2xl">
            Unlock your journals
          </h2>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground/75">
            Membership opens every journal, every prompt and your private reflection history.
            Anything you write stays yours alone, and is never read or analysed automatically.
          </p>
          <div className="mt-5 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap">
            <Link
              to="/membership"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-6 py-3 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] transition hover:brightness-105"
            >
              <Crown className="h-4 w-4" aria-hidden /> Become a Member
            </Link>
            {!isAuthed && (
              <Link
                to="/login"
                search={{ redirect: "/journals" }}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-gold/40 bg-card/80 px-6 py-3 text-sm font-medium text-foreground/85 transition hover:border-gold hover:text-foreground"
              >
                <LogIn className="h-4 w-4" aria-hidden /> I already have an account
              </Link>
            )}
          </div>
        </section>

        <section className="mt-8" aria-labelledby="locked-library-heading">
          <h2
            id="locked-library-heading"
            className="font-display text-[1.35rem] leading-tight text-foreground sm:text-2xl"
          >
            What&apos;s inside
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3">
            {JOURNALS.map((j) => (
              <Link
                key={j.to}
                to="/membership"
                aria-label={`${j.label} — unlock with Membership`}
                className="group relative flex flex-col gap-2 overflow-hidden rounded-[20px] border border-border/70 p-4 shadow-sm transition active:scale-[0.99] sm:flex-row sm:items-start sm:gap-3"
                style={{
                  backgroundImage: `linear-gradient(160deg, ${journalAccent(j.to).tint} 0%, var(--card) 68%)`,
                }}
              >
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl"
                  style={{
                    backgroundColor: "var(--card)",
                    color: journalAccent(j.to).ink,
                    boxShadow: `inset 0 0 0 1px ${journalAccent(j.to).ink}`,
                  }}
                >
                  <j.icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="block font-display text-[1.05rem] leading-snug text-foreground">
                    {j.label}
                  </span>
                  <span className="mt-1 hidden text-[13px] leading-relaxed text-foreground/70 sm:block">
                    {j.blurb}
                  </span>
                  <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-card px-2 py-0.5 text-[11px] font-semibold text-[var(--gold-deep)]">
                    <Lock className="h-3 w-3" aria-hidden /> Members only
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-8">
          <Shelf
            id="locked-sample-prompts"
            title="A few of the prompts"
            hint="Three of the 172+ prompts waiting inside."
            cols={3}
            cardWidth="sm"
          >
            {SAMPLE_PROMPTS.map((p) => (
              <div
                key={p.id}
                className="flex flex-col rounded-[20px] border border-gold/25 bg-card p-4 shadow-sm"
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--gold-deep)]">
                  {DEPTH_LABELS[p.depth]}
                </span>
                <span className="mt-2 text-[15px] leading-relaxed text-foreground">{p.text}</span>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-muted-foreground">
                  <Lock className="h-3.5 w-3.5" aria-hidden /> Write about this with Membership
                </span>
              </div>
            ))}
          </Shelf>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/membership"
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-6 py-3 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] transition hover:brightness-105"
          >
            Unlock the journals <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </div>
  );
}
